import { QN_ARTICLE_COUNT_PER_PAGE } from "@config/quickNews";
import { QUICKNEWS_RECOMMENDATION_FLAG } from "@helper/getEnvVariables";
import {
  getArticlesByIds,
  getMoreRecommendedArticleFeeds,
} from "@helper/getRecommendation";
import logger from "@src/logger/Logger";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import {
  AdsCardProps,
  FeedItem,
  FinishCardProps,
  QNCardType,
  QuizCardProps,
  VideoItem,
} from "@typings/QuickNews";
import { useCallback, useMemo, useRef, useState } from "react";

import { getMoreFeeds, getMoreVideos } from "./QnLoadMore";

// --------------------------- Constants -------------------------------
export const END_CARD: FinishCardProps[] = [
  {
    id: `feed-end-success`,
    title: "Quick News Feed ended",
    name: "Quick News Feed ended",
    endCard: "Feeds Ended",
  },
];

const QUIZ_ITEM: QuizCardProps = {
  id: "kuiz-bh",
  title: "Kuiz BH",
  quizzes: "Random 10 Quizzes",
};

// --------------------------- Ad Helpers ------------------------------
const generateAdItems = (count: number): AdsCardProps[] =>
  Array.from({ length: count }, (_, i) => ({
    id: `imu2-card-${i + 1}`,
    adIndex: i + 1,
    title: "IMU2",
    name: "IMU2",
    adType: "IMU2",
  }));

export const insertAds = (items: FeedItem[]): FeedItem[] => {
  const withAds: FeedItem[] = [];
  const adItems = generateAdItems(Math.ceil(items.length / 4));

  items.forEach((item, index) => {
    withAds.push(item);
    if ((index + 1) % 3 === 0 && adItems.length > 0) {
      withAds.push(adItems.shift() as AdsCardProps);
    }
  });

  return withAds;
};

// ------------------------- Infinite Content Hook ---------------------
interface UseInfiniteContentReturn {
  content: FeedItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  isInitialized: boolean;
  loadMoreOlder: () => void;
  pageOffset: number;
}

export const useInfiniteContent = ({
  Stories,
  initialActiveTab,
  initialId,
}: {
  Stories: ProcessedArticleData[];
  initialActiveTab: QNCardType;
  initialId?: string;
}): UseInfiniteContentReturn => {
  const recommendedArticleIdsRef = useRef<string[]>([]);
  const recommendationPageRef = useRef(1);

  const initialContent = useMemo(() => {
    try {
      const merged = [...Stories];
      return insertAds(merged);
    } catch (err) {
      logger.error("Error computing initial content");
      return [];
    }
  }, [Stories]);

  const [content, setContent] = useState<FeedItem[]>(
    Stories.length > 0 ? initialContent : [],
  );
  const [articles, setArticles] = useState<ProcessedArticleData[]>(
    Stories.length > 0 ? Stories : [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [pageOffset, setPageOffset] = useState(0);
  const [quizAdded, setQuizAdded] = useState(false);
  const [isInitialized] = useState(true);
  const [loadOlderArticles, setLoadOlderArticles] = useState(false);

  const getArticleIdsForPage = useCallback((currentPageOffset: number) => {
    const startIndex = (currentPageOffset - 1) * QN_ARTICLE_COUNT_PER_PAGE;
    const endIndex = startIndex + QN_ARTICLE_COUNT_PER_PAGE;
    return recommendedArticleIdsRef.current.slice(startIndex, endIndex);
  }, []);

  const fetchAndCacheRecommendationIds = useCallback(async () => {
    const { articleIDs, pagination } = await getMoreRecommendedArticleFeeds(
      recommendationPageRef.current,
    );

    if (articleIDs.length > 0) {
      const uniqueIDs = articleIDs.filter(
        (id) => !recommendedArticleIdsRef.current.includes(id),
      );
      if (uniqueIDs.length > 0) {
        recommendedArticleIdsRef.current = [
          ...recommendedArticleIdsRef.current,
          ...uniqueIDs,
        ];
      }
    }

    recommendationPageRef.current =
      pagination.next && pagination.next > 0
        ? pagination.next
        : recommendationPageRef.current + 1;
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || !isInitialized) return;

    setLoading(true);
    setError(null);

    let newArticles = [] as ProcessedArticleData[];
    try {
      const newLoadMoreCount = loadMoreCount + 1;
      setLoadMoreCount(newLoadMoreCount);

      if (
        QUICKNEWS_RECOMMENDATION_FLAG &&
        QUICKNEWS_RECOMMENDATION_FLAG == "1"
      ) {
        const nextPageOffset = pageOffset + 1;

        if (nextPageOffset == 1 || nextPageOffset % 3 === 0) {
          await fetchAndCacheRecommendationIds();
        }

        let pageArticleIds = getArticleIdsForPage(nextPageOffset);
        if (pageArticleIds.length === 0) {
          await fetchAndCacheRecommendationIds();
          pageArticleIds = getArticleIdsForPage(nextPageOffset);
        }

        if (pageArticleIds.length === 0) {
          setHasMore(false);
          return;
        }

        newArticles = await getArticlesByIds(pageArticleIds);

        const hasCompletePage =
          pageArticleIds.length === QN_ARTICLE_COUNT_PER_PAGE &&
          newArticles.length === QN_ARTICLE_COUNT_PER_PAGE;
        setHasMore(hasCompletePage);
        setPageOffset(nextPageOffset);
      } else {
        const lastArticle = articles.at(-1);
        const requestData: MoreArticlesRequestData = {
          sort: lastArticle?.sort,
        };

        newArticles = await getMoreFeeds(requestData, loadOlderArticles);
        setArticles((prev) => [...prev, ...newArticles]);

        if (newArticles.length < QN_ARTICLE_COUNT_PER_PAGE) {
          setHasMore(false);
        }
      }
      let combinedItems: FeedItem[] = [...newArticles];

      // Exclude initial active item
      const excludedId =
        initialActiveTab === "MyFeed" && initialId ? initialId : undefined;
      if (excludedId)
        combinedItems = combinedItems.filter((item) => item.id !== excludedId);

      setContent((prevContent) => {
        const updatedContent = prevContent.filter(
          (item) => !("adType" in item),
        );
        updatedContent.push(...combinedItems);

        if (newLoadMoreCount === 1 && !quizAdded) {
          const randomPosition = 5 + Math.floor(Math.random() * 6); // 5th-10th
          updatedContent.splice(randomPosition, 0, QUIZ_ITEM);
          setQuizAdded(true);
        }

        return insertAds(updatedContent);
      });
      if (newArticles.length < QN_ARTICLE_COUNT_PER_PAGE) {
        setContent((prev) => [...prev, ...END_CARD]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more content",
      );
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMore,
    isInitialized,
    loadMoreCount,
    quizAdded,
    initialActiveTab,
    initialId,
    pageOffset,
    getArticleIdsForPage,
    fetchAndCacheRecommendationIds,
    articles,
    loadOlderArticles,
  ]);

  const loadMoreOlder = useCallback(() => {
    setLoadOlderArticles(true);
    setHasMore(true);
  }, []);

  return {
    content,
    loading,
    error,
    hasMore,
    loadMore,
    isInitialized,
    pageOffset,
    loadMoreOlder,
  };
};

// ------------------------- Infinite Videos Hook ---------------------
interface UseInfiniteVideosReturn {
  videos: VideoItem[];
  loading: boolean;
  error: string | null;
  hasMoreVideos: boolean;
  loadMoreVideos: () => Promise<void>;
}

export const useInfiniteVideos = ({
  bcVideos,
  initialId,
  initialActiveTab,
}: {
  bcVideos?: LatestVideoResponse[];
  initialId?: string;
  initialActiveTab: QNCardType;
}): UseInfiniteVideosReturn => {
  const [videos, setVideos] = useState<VideoItem[]>(bcVideos || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);

  const loadMoreVideos = useCallback(async () => {
    if (loading || !hasMoreVideos) return;

    setLoading(true);
    setError(null);

    try {
      const newLoadMoreCount = loadMoreCount + 1;
      setLoadMoreCount(newLoadMoreCount);

      const offset = videos.length;
      const newVideos = await getMoreVideos(offset);

      const excludedId = initialActiveTab === "Videos" ? initialId : undefined;
      const filteredVideos = excludedId
        ? newVideos.filter((v) => v.id !== excludedId)
        : newVideos;

      setVideos((prev) => [...prev, ...filteredVideos]);
      if (!newVideos.length) setVideos((prev) => [...prev, ...END_CARD]);
      setHasMoreVideos(newVideos.length > 0);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more videos",
      );
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMoreVideos,
    loadMoreCount,
    initialId,
    initialActiveTab,
    videos,
  ]);

  return { videos, loading, error, hasMoreVideos, loadMoreVideos };
};
